import { z } from 'zod'
import type { FormSubmitEvent } from '#ui/types'

type AuthActionOptions = {
  redirectOnSuccess?: boolean
}

export default function useUser() {
  const pb = useNuxtApp().$pb
  const user = ref()
  const toast = useToast()
  const isAdmin = computed(() => user.value?.role === 'admin')
  pb.authStore.onChange(() => {
    user.value = pb.authStore.model
  }, true)

  function logout() {
    pb.authStore.clear()
    user.value = undefined
    navigateTo('/')
  }

  function goToLogin() {
    navigateTo('/login')
  }

  function goToRegister() {
    navigateTo('/login?type=register')
  }

  function isUsers(id: string, collection: string) {
    if (!id || !collection)
      return false
    return user.value?.[collection]?.some(b => b === id) ?? false
  }




  const resetPasswordRequestSchema = z.object({
    email: z.email()
  })

  async function requestPassword(event: FormSubmitEvent<z.output<typeof resetPasswordRequestSchema>>) {
    //  pb.collection(collectionIdOrName).confirmPasswordReset(resetToken, newPassword, newPasswordConfirm, options = {});
    try {
      await pb.collection('users').requestPasswordReset(event.data.email)
      toast.add({
        id: 'password-reset',
        title: 'Password Reset',
        description: 'A password reset link has been sent to your email.',
        icon: 'i-ri-check-line'
      })
    } catch (err) {
      toast.add({
        id: 'login-error',
        title: 'Login Error',
        description: err?.data?.message,
        color: 'error',
        icon: 'i-ri-error-warning-line'
      })
    }
  }

  const resetPasswordConfirmSchema = z.object({
    password: z.string().min(8, 'Password must be at least 8 characters long'),
    passwordConfirm: z.string().min(8, 'Password must be at least 8 characters long')
  }).refine(data => data.passwordConfirm === data.password, {
    message: 'Passwords must match', path: ['passwordConfirm']
  })


  async function savePassword(event: FormSubmitEvent<z.output<typeof resetPasswordConfirmSchema>>) {
    //  pb.collection(collectionIdOrName).confirmPasswordReset(resetToken, newPassword, newPasswordConfirm, options = {});
    try {
      await pb.collection('users').confirmPasswordReset(token.value, event.data.password, event.data.passwordConfirm)
      toast.add({
        id: 'password-reset',
        title: 'Password Saved',
        description: 'Your new password has been saved. Please login.',
        icon: 'i-ri-check-line'
      })
      navigateTo('/login')
    } catch (err) {
      toast.add({
        id: 'login-error',
        title: 'Login Error',
        description: err?.data?.message,
        color: 'error',
        icon: 'i-ri-error-warning-line'
      })
    }
  }

  const router = useRouter()
  const token = computed(() => router.currentRoute.value.query.i as string)
  const loginSchema = z.object({
    email: z.email('Invalid email address').min(1, 'Email is required'),
    password: z.string().min(6, 'Password must be at least 6 characters'),
  })

  async function login(
    event: FormSubmitEvent<z.output<typeof loginSchema>>,
    options: AuthActionOptions = {}
  ): Promise<boolean> {
    const redirectOnSuccess = options.redirectOnSuccess ?? true

    try {
      await pb.collection('users').authWithPassword(event.data.email, event.data.password)
      toast.add({
        title: 'Logged In',
        description: 'Successfully logged in'
      })

      if (redirectOnSuccess) {
        if (isAdmin.value) {
          navigateTo('/admin/dashboard')
        } else {
          navigateTo('/dashboard')
        }
      }

      return true
    } catch (error) {
      console.error('Login failed:', error)
      toast.add({
        title: 'Login Failed',
        description: 'Please check your credentials and try again.',
        color: 'error'
      })

      return false
    }
  }


  const registerSchema = z.object({
    name: z.string().min(1, 'Name is required'),
    email: z.email('Invalid email address').min(1, 'Email is required'),
    password: z.string().min(6, 'Password must be at least 6 characters'),
    passwordConfirm: z.string().min(6, 'Confirm Password must be at least 6 characters'),
  }).refine(data => data.password === data.passwordConfirm, {
    message: 'Passwords do not match',
    path: ['passwordConfirm'],
  })

  async function register(
    event: FormSubmitEvent<z.output<typeof registerSchema>>,
    options: AuthActionOptions = {}
  ): Promise<boolean> {
    const redirectOnSuccess = options.redirectOnSuccess ?? true

    try {
      await pb.collection('users').create({
        email: event.data.email,
        password: event.data.password,
        passwordConfirm: event.data.passwordConfirm,
        name: event.data.name
      })
      toast.add({
        title: 'Registered',
        description: 'Successfully registered'
      })

      const isLoggedIn = await login(
        { data: { email: event.data.email, password: event.data.password } } as FormSubmitEvent<
          z.output<typeof loginSchema>
        >,
        { redirectOnSuccess }
      )

      return isLoggedIn
    } catch (error) {
      console.error('Registration failed:', error)
      toast.add({
        title: 'Registration Failed',
        description: 'Please check your credentials and try again.',
        color: 'error'
      })

      return false
    }
  }

  async function updateUserDetails(details: Record<string, any>) {
    try {
      const updatedUser = await pb.collection('users').update(user.value.id, details)
      user.value = updatedUser
      toast.add({
        title: 'Profile Updated',
        description: 'Your profile has been updated successfully.'
      })
    } catch (error) {
      console.error('Profile update failed:', error)
      toast.add({
        title: 'Update Failed',
        description: 'There was an error updating your profile. Please try again.',
        color: 'error'
      })
    }
  }



  return {
    user: computed(() => user.value),
    userId: computed(() => user.value?.id),
    isUsers,
    logout,
    isLoggedIn: computed(() => user.value?.id),
    isAdmin,
    role: computed(() => user.value?.role),
    loginSchema,
    registerSchema,
    resetPasswordRequestSchema,
    resetPasswordConfirmSchema,
    goToLogin,
    goToRegister,
    requestPassword,
    savePassword,
    token,
    login,
    register,
    updateUserDetails
  }
}
